import { useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  IconButton,
  Select,
  Spinner,
  Switch,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { AddIcon, ArrowBackIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { MdLibraryBooks } from "react-icons/md";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import ConfirmDialog from "@/components/consignado/Cobranca/components/ConfirmDialog";
import ComoUtilizarModal from "@/components/consignado/Cobranca/VinculosAtivosRedesign/ComoUtilizarModal";
import { CONCEITOS_TEMPLATE, PASSOS_LISTAGEM_TEMPLATES } from "./comoUtilizar.content";
import {
  CANAL_LABEL,
  CanalComunicacao,
  CatalogoDisparo,
  ContextoDisparo,
  DisparoTemplateItem,
  MODO_TEMPLATE_LABEL,
  ModoTemplate,
  PROVEDOR_LABEL,
  contextosDisponiveis,
  formatDateBr,
  rotuloContexto,
} from "@/models/consignado/disparo.model";
import { fieldFont } from "./styleTokens";

export type TemplatesFiltros = {
  ativo?: boolean;
  contexto?: ContextoDisparo;
  canal?: CanalComunicacao;
};

type Props = {
  templates: DisparoTemplateItem[];
  /** Fonte dos contextos do filtro e da coluna — o enum só conhece três. */
  catalogo?: CatalogoDisparo;
  filtros: TemplatesFiltros;
  isLoading?: boolean;
  isError?: boolean;
  togglingId?: string | null;
  onFiltrosChange: (filtros: TemplatesFiltros) => void;
  onNovo: () => void;
  onEditar: (template: DisparoTemplateItem) => void;
  onExcluir: (template: DisparoTemplateItem) => void;
  onToggleAtivo: (template: DisparoTemplateItem) => void;
  /** Volta para a listagem de disparos, de onde os templates são abertos. */
  onVoltar?: () => void;
};

const tableHeaderStyles = {
  color: "white",
  textAlign: "left" as const,
  fontSize: "10px",
  py: 4,
  px: 3,
  fontFamily: "Lexend Deca",
  _notLast: { borderRight: "1px solid", borderColor: "whiteAlpha.400" },
};

const tableCellStyles = {
  textAlign: "left" as const,
  fontFamily: "Lexend Deca",
  fontSize: "12px",
  fontWeight: "normal" as const,
  px: 3,
  py: 3,
};

/** Um resumo curto do conteúdo, pra grade não abrir cada template. */
const resumo = (t: DisparoTemplateItem): string => {
  if (t.conteudo.modo === ModoTemplate.REFERENCIA_EXTERNA) {
    return t.conteudo.referenciaExterna?.identificador ?? "—";
  }
  const texto = (t.conteudo.conteudo ?? "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return texto.length > 80 ? `${texto.slice(0, 80)}…` : texto || "—";
};

/**
 * Listagem de templates reutilizáveis (GET /disparos/templates).
 *
 * Sem paginação de propósito: o endpoint não tem pagina/limite — devolve a
 * lista filtrada por ativo, contexto, canal e provedor.
 */
export default function TemplatesView({
  templates,
  catalogo,
  filtros,
  isLoading,
  isError,
  togglingId,
  onFiltrosChange,
  onNovo,
  onEditar,
  onExcluir,
  onToggleAtivo,
  onVoltar,
}: Props) {
  const backgroundStyle = useConditionalBackground();
  const deleteDialog = useDisclosure();
  const [pendingDelete, setPendingDelete] = useState<DisparoTemplateItem | null>(null);
  const [pendingDesativar, setPendingDesativar] = useState<DisparoTemplateItem | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    onExcluir(pendingDelete);
    setPendingDelete(null);
    deleteDialog.onClose();
  };

  const confirmDesativar = () => {
    if (!pendingDesativar) return;
    onToggleAtivo(pendingDesativar);
    setPendingDesativar(null);
  };

  return (
    <Box w="100%" padding={{ base: "2", md: "4" }}>
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: "4", md: "6" }}
        color="white"
        boxShadow="md"
        width="100%"
        mb={6}
      >
        <Flex
          align={{ base: "flex-start", md: "center" }}
          justify="space-between"
          direction={{ base: "column", md: "row" }}
          gap={4}
        >
          <Flex align="center">
            {onVoltar && (
              <IconButton
                aria-label="Voltar para os disparos"
                icon={<ArrowBackIcon />}
                variant="ghost"
                color="white"
                _hover={{ bg: "whiteAlpha.300" }}
                onClick={onVoltar}
                mr={3}
              />
            )}
            <MdLibraryBooks size="2rem" />
            <Box ml={3}>
              <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" fontFamily="Lexend Deca">
                Templates de mensagem
              </Text>
              <Text fontSize="sm" opacity={0.85} fontFamily="Lexend Deca">
                Textos reutilizáveis pelos disparos
              </Text>
            </Box>
          </Flex>

          <Flex align="center" gap={3}>
            <ComoUtilizarModal
              tituloTela="Templates de mensagem"
              passos={PASSOS_LISTAGEM_TEMPLATES}
              conceitos={CONCEITOS_TEMPLATE}
            />
            <Button
              leftIcon={<AddIcon />}
              bg="white"
              color="primary"
              _hover={{ bg: "whiteAlpha.800" }}
              onClick={onNovo}
              fontFamily="Lexend Deca"
            >
              Novo template
            </Button>
          </Flex>
        </Flex>
      </Box>

      <Box w="100%" bg="white" borderRadius="xl" boxShadow="sm" p={{ base: 2, md: 4 }}>
        <Flex justify="space-between" align="center" gap={3} mb={4} wrap="wrap">
          <Flex gap={3} align="center" wrap="wrap">
            <Select
              size="sm"
              w="170px"
              borderRadius="md"
              {...fieldFont}
              value={filtros.ativo === undefined ? "" : String(filtros.ativo)}
              onChange={(e) =>
                onFiltrosChange({
                  ...filtros,
                  ativo: e.target.value === "" ? undefined : e.target.value === "true",
                })
              }
            >
              <option value="">Ativos e inativos</option>
              <option value="true">Só ativos</option>
              <option value="false">Só inativos</option>
            </Select>

            <Select
              size="sm"
              w="210px"
              borderRadius="md"
              {...fieldFont}
              value={filtros.contexto ?? ""}
              onChange={(e) =>
                onFiltrosChange({
                  ...filtros,
                  contexto: (e.target.value || undefined) as ContextoDisparo | undefined,
                })
              }
            >
              <option value="">Todos os contextos</option>
              {contextosDisponiveis(catalogo).map((c) => (
                <option key={c} value={c}>
                  {rotuloContexto(c, catalogo)}
                </option>
              ))}
            </Select>

            <Select
              size="sm"
              w="160px"
              borderRadius="md"
              {...fieldFont}
              value={filtros.canal ?? ""}
              onChange={(e) =>
                onFiltrosChange({
                  ...filtros,
                  canal: (e.target.value || undefined) as CanalComunicacao | undefined,
                })
              }
            >
              <option value="">Todos os canais</option>
              {Object.values(CanalComunicacao).map((c) => (
                <option key={c} value={c}>
                  {CANAL_LABEL[c]}
                </option>
              ))}
            </Select>
          </Flex>

          <Tag colorScheme="gray" fontFamily="Lexend Deca">
            Total: {templates.length}
          </Tag>
        </Flex>

        {isLoading ? (
          <Flex w="100%" justify="center" py={12}>
            <Spinner color="primary" />
          </Flex>
        ) : isError ? (
          <Flex w="100%" justify="center" py={12}>
            <Text color="red.500" fontFamily="Lexend Deca">
              Não foi possível carregar os templates.
            </Text>
          </Flex>
        ) : templates.length === 0 ? (
          <Flex w="100%" justify="center" py={12}>
            <Text color="gray.500" fontFamily="Lexend Deca">
              Nenhum template cadastrado. Clique em "Novo template" para criar o primeiro.
            </Text>
          </Flex>
        ) : (
          <TableContainer borderRadius="lg">
            <Table variant="simple" sx={{ tableLayout: "fixed", w: "100%", minW: "1080px" }}>
              <Thead {...backgroundStyle}>
                <Tr>
                  <Th {...tableHeaderStyles} w="110px" textAlign="center">AÇÕES</Th>
                  <Th {...tableHeaderStyles} w="80px">ATIVO</Th>
                  <Th {...tableHeaderStyles} w="230px">NOME</Th>
                  <Th {...tableHeaderStyles} w="190px">CONTEXTO</Th>
                  <Th {...tableHeaderStyles} w="200px">CANAL / PROVEDOR</Th>
                  <Th {...tableHeaderStyles} w="320px">MENSAGEM</Th>
                </Tr>
              </Thead>
              <Tbody>
                {templates.map((t) => (
                  <Tr key={t.id} _odd={{ bg: "blackAlpha.50" }} _hover={{ bg: "gray.100" }}>
                    <Td {...tableCellStyles} textAlign="center">
                      <Flex gap={1} justify="center">
                        <Tooltip label="Editar" placement="top">
                          <IconButton
                            aria-label="Editar template"
                            icon={<EditIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="gray"
                            onClick={() => onEditar(t)}
                          />
                        </Tooltip>
                        <Tooltip label="Excluir" placement="top">
                          <IconButton
                            aria-label="Excluir template"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => {
                              setPendingDelete(t);
                              deleteDialog.onOpen();
                            }}
                          />
                        </Tooltip>
                      </Flex>
                    </Td>
                    <Td {...tableCellStyles}>
                      <Switch
                        isChecked={t.ativo}
                        colorScheme="green"
                        isDisabled={togglingId === t.id}
                        // Ligar vai direto; desligar pergunta, porque os disparos
                        // vinculados passam a falhar no envio (o vínculo é vivo).
                        onChange={() => (t.ativo ? setPendingDesativar(t) : onToggleAtivo(t))}
                      />
                    </Td>
                    <Td {...tableCellStyles}>
                      <Text whiteSpace="normal" wordBreak="break-word" lineHeight="1.3" fontFamily="Lexend Deca">
                        {t.nome}
                      </Text>
                      <Text fontSize="10px" color="gray.500" fontFamily="Lexend Deca">
                        atualizado em {formatDateBr(t.atualizadoEm)}
                      </Text>
                    </Td>
                    <Td {...tableCellStyles}>{rotuloContexto(t.contexto, catalogo)}</Td>
                    <Td {...tableCellStyles}>
                      <Flex direction="column" gap={1} align="flex-start">
                        <Tag size="sm" colorScheme="green" borderRadius="md" fontFamily="Lexend Deca">
                          {CANAL_LABEL[t.canal]} · {PROVEDOR_LABEL[t.provedor]}
                        </Tag>
                        <Text fontSize="10px" color="gray.500" fontFamily="Lexend Deca">
                          {MODO_TEMPLATE_LABEL[t.modo]}
                        </Text>
                      </Flex>
                    </Td>
                    <Td {...tableCellStyles}>
                      <Text
                        fontSize="12px"
                        color="gray.600"
                        whiteSpace="normal"
                        wordBreak="break-word"
                        lineHeight="1.3"
                        fontFamily="Lexend Deca"
                      >
                        {resumo(t)}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent fontFamily="Lexend Deca">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Excluir template
            </AlertDialogHeader>
            <AlertDialogBody>
              Excluir <strong>{pendingDelete?.nome}</strong>? Não dá para desfazer. Se algum disparo
              usa este template, a exclusão é recusada — troque a mensagem desses disparos antes.
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <Button ref={cancelRef} onClick={deleteDialog.onClose} variant="ghost">
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={confirmDelete}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <ConfirmDialog
        isOpen={!!pendingDesativar}
        onClose={() => setPendingDesativar(null)}
        onConfirm={confirmDesativar}
        titulo="Desativar template?"
        descricao={
          <>
            Enquanto <strong>{pendingDesativar?.nome}</strong> estiver inativo, os disparos que usam
            este template falham no envio e não podem ser salvos nem ativados. Ele também deixa de
            aparecer para novos disparos.
          </>
        }
        confirmLabel="Desativar"
        cancelLabel="Manter ativo"
      />
    </Box>
  );
}
